import type { PluginManifest, SignatureManifest, TrustedKey, VerifyResult } from '@slate/plugin-api';
import { sha256Hex, recomputeContentHash, canonicalMessage, base64ToBytes } from './canonical';

export interface VerifyInput {
  manifest: Pick<PluginManifest, 'id' | 'version' | 'main'>;
  signature: SignatureManifest;
  readFile(path: string): Promise<Uint8Array>;
  trustedKeys: TrustedKey[];
}

const enc = new TextEncoder();

/** Verify a sealed plugin's integrity. FAIL CLOSED: any thrown error, missing
 *  signer, hash mismatch, or bad signature returns ok:false. */
export async function verifySignature(input: VerifyInput): Promise<VerifyResult> {
  try {
    const { signature: sig, manifest, trustedKeys } = input;
    if (sig.alg !== 'ed25519') return { ok: false, reason: 'unsupported signature algorithm' };
    if (sig.id !== manifest.id || sig.version !== manifest.version)
      return { ok: false, reason: 'signature id/version does not match manifest' };
    if (!Array.isArray(sig.files) || sig.files.length === 0)
      return { ok: false, reason: 'signature lists no files' };

    const main = manifest.main ?? 'worker.js';
    const covered = new Set(sig.files.map((f) => f.path));
    if (!covered.has('manifest.json') || !covered.has(main))
      return { ok: false, reason: 'signature must cover manifest.json and the worker entry' };

    const key = trustedKeys.find((k) => k.keyId === sig.keyId);
    if (!key) return { ok: false, reason: `untrusted signer key id "${sig.keyId}"` };

    // Recompute every file hash (file-tamper detection).
    for (const f of sig.files) {
      const bytes = await input.readFile(f.path);
      if (sha256Hex(bytes) !== f.sha256) return { ok: false, reason: `hash mismatch: ${f.path}` };
    }
    // Recompute content hash (file-list-tamper detection).
    if (recomputeContentHash(sig.files) !== sig.contentHash)
      return { ok: false, reason: 'content hash mismatch' };

    // Verify the Ed25519 signature over the canonical message (lazy crypto).
    const { ed25519 } = await import('@noble/curves/ed25519');
    const { hexToBytes } = await import('@noble/hashes/utils');
    const msg = enc.encode(canonicalMessage(sig));
    const ok = ed25519.verify(base64ToBytes(sig.signature), msg, hexToBytes(key.publicKeyHex));
    return ok ? { ok: true } : { ok: false, reason: 'signature verification failed' };
  } catch (e) {
    return { ok: false, reason: `verify error: ${String(e)}` };
  }
}
